/**
 * OBRYSY — prostokąty węzłów w milimetrach.
 *
 * Jedno źródło geometrii dla dwóch wołających, którzy MUSZĄ się zgadzać:
 *   • warstwa uchwytów na płótnie edytora (gdzie klikam, żeby złapać blok),
 *   • walidator („ten blok wyjeżdża poza etykietę").
 * Gdyby liczyły osobno, uchwyt stałby gdzie indziej niż blok, a walidator
 * zgłaszałby wyjazd tam, gdzie oko go nie widzi.
 *
 * SZACOWANIE JEST OZNACZONE OSOBNO DLA KAŻDEJ OSI. Tekst bez `maxWidthMm`
 * renderuje się w facsimile z `white-space: nowrap`, więc jego SZEROKOŚĆ zna
 * dopiero przeglądarka (a na drukarce termicznej — metryka fontu bitmapowego,
 * która jest inna). Tekst Z `maxWidthMm` ma to odwrotnie: szerokość jest
 * zapisana w układzie, a niepewna staje się WYSOKOŚĆ, bo liczba wierszy
 * wychodzi dopiero z łamania treści. Wysokość pozostałych rodzajów jest
 * dokładna: wynika z `sizePt` i `line-height: 1.15`, z `heightMm` kodu,
 * z `sizeMm` QR-a albo z `(wierszy - 1) × stepMm` powtórzenia.
 *
 * Jedna wspólna flaga `szacowany` kosztowała dokładnie tyle: wyciszała także
 * sprawdzenie POCZĄTKU obrysu i DOLNEJ krawędzi, więc tekst na `yMm: 7193`
 * i kod kreskowy na `xMm: -2398` przechodziły walidację czysto i szły do
 * enkodera. Dlatego flagi są dwie, a walidator pyta osobno o każdą krawędź.
 */

import { UnknownPrintNodeError } from '../errors';
import { obszarZadrukuMm } from '../ir';
import type { PrintMedia } from '../types';

import { podstaw, type KontekstPodstawienia } from './podstawianie';
import type { KsztaltTeraz, LayoutNode, PrintLayout, Wyrazenie } from './schemat';

export interface Obrys {
  xMm: number;
  yMm: number;
  widthMm: number;
  heightMm: number;
  /** Szerokość policzona z przybliżenia metryki fontu — prawa krawędź niepewna. */
  szacowanaSzerokosc: boolean;
  /**
   * Wysokość policzona z przybliżenia — dolna krawędź niepewna.
   *
   * Pole powstało „na pierwszy węzeł o wysokości zależnej od danych". Tym
   * węzłem jest tekst z `maxWidthMm`: ile wierszy zajmie blok, wiadomo dopiero
   * po złamaniu treści, a treść w edytorze jest jeszcze niepodstawiona.
   */
  szacowanaWysokosc: boolean;
}

export interface ObrysWezla extends Obrys {
  nodeId: string;
  kind: LayoutNode['kind'];
  /** Ścieżka w układzie — ta sama konwencja co w `BladUkladu.sciezka`. */
  sciezka: string;
}

/** 1 pt = 1/72 cala; 1 cal = 25,4 mm. */
export function ptNaMm(pt: number): number {
  return (pt * 25.4) / 72;
}

/**
 * Przybliżona szerokość znaku. 0,55 × wysokość fontu to ta sama stała, której
 * używa enkoder ZPL do łamania linii (`znakiNaSzerokosc` w `encoders/zpl.ts`)
 * — jedno przybliżenie w całym pakiecie, nie dwa rozjeżdżające się.
 */
const SZEROKOSC_ZNAKU = 0.55;

/** Długość sformatowanej chwili wydruku w znakach — format jest zamknięty. */
const DLUGOSC_TERAZ: Record<KsztaltTeraz, number> = {
  data: '31.12.2026'.length,
  godzina: '23:59'.length,
  data_godzina: '31.12.2026 23:59'.length,
};

/** Wysokość wiersza tekstu w mm (`line-height: 1.15` z facsimile). */
function wysokoscWierszaMm(sizePt: number): number {
  return ptNaMm(sizePt) * 1.15;
}

/**
 * Ile wierszy zajmie `znaki` znaków w bloku szerokim na `maxWidthMm`.
 *
 * Zawsze co najmniej jeden: pusty blok też zajmuje wiersz, a wynik 0 dawałby
 * obrys o zerowej wysokości, czyli węzeł nie do złapania na płótnie.
 */
function wierszeBloku(znaki: number, maxWidthMm: number, sizePt: number): number {
  const szerZnaku = ptNaMm(sizePt) * SZEROKOSC_ZNAKU;
  const naWiersz = Math.max(1, Math.floor(maxWidthMm / szerZnaku));
  return Math.max(1, Math.ceil(znaki / naWiersz));
}

/** Długość wyrażenia w znakach — literały dosłownie, wiązania z przykładu albo z nazwy. */
function dlugoscWyrazenia(w: Wyrazenie, ctx: KontekstPodstawienia | null): number {
  if (ctx) return podstaw(w, ctx).length;
  // Bez kontekstu (edytor przed podstawieniem) długość wiązania szacujemy
  // nazwą zmiennej — krótsza od typowej wartości, ale stabilna.
  return w.reduce((acc, t) => {
    if ('lit' in t) return acc + t.lit.length;
    if ('var' in t) return acc + t.var.length;
    if ('pole' in t) return acc + t.pole.length;
    // Chwila wydruku ma długość ZNANĄ z góry — format jest zamknięty
    // (`31.12.2026`, `23:59`), więc tu nie ma czego szacować. Liczenie jej
    // jako zera dawało obrys węższy od napisu i blok „mieszczący się"
    // w etykiecie, który drukarka ucina.
    if ('teraz' in t) return acc + DLUGOSC_TERAZ[t.teraz];
    return acc;
  }, 0);
}

/** Obrys jednego węzła. `ctx` (opcjonalny) daje dokładniejsze szacowanie tekstu. */
export function obrysWezla(node: LayoutNode, ctx: KontekstPodstawienia | null = null): Obrys {
  switch (node.kind) {
    case 'text': {
      const sizePt = node.sizePt ?? 8;
      const h = wysokoscWierszaMm(sizePt);
      if (node.maxWidthMm !== undefined) {
        // Blok ZAWIJA, więc jego wysokość to liczba wierszy × wysokość wiersza
        // — nie jeden wiersz. Zwracanie `h` z flagą „pewna" mówiło dwa
        // nieprawdziwe zdania naraz: uchwyt na płótnie obejmował pierwszy
        // wiersz czterowierszowego akapitu (nie dało się go złapać za dół),
        // a walidator mierzył dolną krawędź tam, gdzie kończył się wiersz
        // pierwszy — więc blok schodzący pod krawędź etykiety przechodził
        // publikację i był ucinany dopiero przez drukarkę.
        //
        // Liczbę wierszy szacujemy TĄ SAMĄ metryką, którą gałąź bez bloku
        // szacuje szerokość (`SZEROKOSC_ZNAKU`), czyli tą, którą łamie enkoder
        // ZPL w `znakiNaSzerokosc`. Stąd flaga wysokości `true`: to jest
        // przybliżenie, a nie liczba zapisana w układzie.
        return {
          xMm: node.xMm,
          yMm: node.yMm,
          widthMm: node.maxWidthMm,
          heightMm: h * wierszeBloku(dlugoscWyrazenia(node.tekst, ctx), node.maxWidthMm, sizePt),
          szacowanaSzerokosc: false,
          szacowanaWysokosc: true,
        };
      }
      const znaki = dlugoscWyrazenia(node.tekst, ctx);
      return {
        xMm: node.xMm,
        yMm: node.yMm,
        widthMm: znaki * ptNaMm(sizePt) * SZEROKOSC_ZNAKU,
        heightMm: h,
        szacowanaSzerokosc: true,
        szacowanaWysokosc: false,
      };
    }

    case 'barcode': {
      // Facsimile ustawia `min-width: max(24, dane.length * 1.1)` mm
      // (`encoders/html.ts`), a pod kodem rysuje wiersz z wartością.
      const znaki = dlugoscWyrazenia(node.dane, ctx);
      const wysokoscCzytelnego = node.showText === false ? 0 : wysokoscWierszaMm(6);
      return {
        xMm: node.xMm,
        yMm: node.yMm,
        widthMm: Math.max(24, znaki * 1.1),
        heightMm: node.heightMm + wysokoscCzytelnego,
        szacowanaSzerokosc: true,
        szacowanaWysokosc: false,
      };
    }

    case 'qr':
      return {
        xMm: node.xMm,
        yMm: node.yMm,
        widthMm: node.sizeMm,
        heightMm: node.sizeMm,
        szacowanaSzerokosc: false,
        szacowanaWysokosc: false,
      };

    case 'line': {
      const t = node.thicknessMm ?? 0.4;
      return {
        xMm: Math.min(node.xMm, node.x2Mm),
        yMm: Math.min(node.yMm, node.y2Mm),
        widthMm: Math.max(t, Math.abs(node.x2Mm - node.xMm)),
        heightMm: Math.max(t, Math.abs(node.y2Mm - node.yMm)),
        szacowanaSzerokosc: false,
        szacowanaWysokosc: false,
      };
    }

    case 'box':
    case 'image':
      return {
        xMm: node.xMm,
        yMm: node.yMm,
        widthMm: node.widthMm,
        heightMm: node.heightMm,
        szacowanaSzerokosc: false,
        szacowanaWysokosc: false,
      };

    case 'repeat': {
      const zrodlo = node.zrodlo;
      // Liczba wierszy zależy od danych, więc obrys powtórzenia jest z natury
      // szacunkiem: bierzemy DEKLAROWANY sufit (maxLines / maxWierszy /
      // liczbę wierszy literalnych), bo to on odpowiada na pytanie „ile
      // miejsca ten blok może zająć na naklejce".
      const wierszy =
        zrodlo.rodzaj === 'literalne'
          ? zrodlo.wiersze.length
          : zrodlo.rodzaj === 'zawijanie'
            ? zrodlo.maxLines
            : zrodlo.maxWierszy;

      const dzieci: LayoutNode[] =
        zrodlo.rodzaj === 'literalne'
          ? zrodlo.wiersze.flat()
          : zrodlo.rodzaj === 'lista'
            ? zrodlo.wiersz
            : [];

      let lewy = Infinity;
      let prawy = -Infinity;
      let wysWiersza = 0;
      if (zrodlo.rodzaj === 'zawijanie') {
        lewy = zrodlo.xMm;
        prawy = zrodlo.xMm + zrodlo.maxChars * ptNaMm(zrodlo.sizePt ?? 8) * SZEROKOSC_ZNAKU;
        wysWiersza = wysokoscWierszaMm(zrodlo.sizePt ?? 8);
      } else {
        for (const d of dzieci) {
          const o = obrysWezla(d, ctx);
          lewy = Math.min(lewy, o.xMm);
          prawy = Math.max(prawy, o.xMm + o.widthMm);
          wysWiersza = Math.max(wysWiersza, o.yMm + o.heightMm);
        }
      }
      if (!Number.isFinite(lewy)) {
        lewy = 0;
        prawy = 0;
      }

      const wysokosc =
        wierszy > 0 ? (wierszy - 1) * node.stepMm + Math.max(wysWiersza, node.stepMm) : 0;
      return {
        xMm: lewy,
        yMm: node.yMm,
        widthMm: Math.max(0, prawy - lewy),
        heightMm: wysokosc,
        szacowanaSzerokosc: true,
        szacowanaWysokosc: false,
      };
    }

    default:
      // Ta funkcja karmi DWÓCH wołających — walidator i warstwę uchwytów
      // płótna. Zwracane `undefined` wysadzało oba (`reading 'szacowany'`),
      // i to zanim którykolwiek zdążył zgłosić „nieznany rodzaj".
      throw new UnknownPrintNodeError((node as { kind?: unknown }).kind);
  }
}

/** Obrysy wszystkich węzłów układu, rekurencyjnie — dla warstwy uchwytów i walidatora. */
export function obrysyUkladu(
  layout: PrintLayout,
  ctx: KontekstPodstawienia | null = null
): ObrysWezla[] {
  const out: ObrysWezla[] = [];

  const zejdz = (node: LayoutNode, sciezka: string, przesuniecieYMm: number): void => {
    const o = obrysWezla(node, ctx);
    out.push({
      ...o,
      yMm: o.yMm + przesuniecieYMm,
      nodeId: node.id,
      kind: node.kind,
      sciezka,
    });
    if (node.kind !== 'repeat') return;

    const zrodlo = node.zrodlo;
    if (zrodlo.rodzaj === 'literalne') {
      zrodlo.wiersze.forEach((wiersz, i) =>
        wiersz.forEach((dziecko, j) =>
          zejdz(
            dziecko,
            `${sciezka}.zrodlo.wiersze[${i}][${j}]`,
            przesuniecieYMm + node.yMm + i * node.stepMm
          )
        )
      );
    } else if (zrodlo.rodzaj === 'lista') {
      // Wiersz szablonowy raz, na pozycji pierwszego wiersza — panel maluje
      // uchwyty na wzorcu, a nie na każdej rozwiniętej pozycji z osobna.
      zrodlo.wiersz.forEach((dziecko, j) =>
        zejdz(dziecko, `${sciezka}.zrodlo.wiersz[${j}]`, przesuniecieYMm + node.yMm)
      );
    }
  };

  layout.nodes.forEach((node, i) => zejdz(node, `nodes[${i}]`, 0));
  return out;
}

/** Tolerancja zaokrąglenia do 0,01 mm (`zaokr`). */
const LUZ_MM = 0.01;

/** Która krawędź obrysu wyjeżdża poza media. */
export type NaruszenieMediow = 'poczatek' | 'prawa' | 'dol';

/**
 * Krawędzie, którymi obrys wyjeżdża poza media — z uwzględnieniem tego, co
 * jest policzone, a co oszacowane.
 *
 * `poczatek` (lewy górny róg) i `dol` sprawdzamy ZAWSZE: obie liczby biorą się
 * wprost z układu. `prawa` tylko dla obrysów o pewnej szerokości, bo tekst bez
 * `maxWidthMm` ma szerokość znaną dopiero przeglądarce — walidator blokujący
 * publikację poprawnych etykiet zostałby wyłączony przez pierwszego
 * użytkownika, a to gorsze niż brak walidatora.
 */
export function naruszeniaMediow(o: Obrys, media: PrintMedia): NaruszenieMediow[] {
  // OBSZAR ZADRUKU, nie wymiary arkusza — to z niego `fragmentHtml` liczy
  // kontener. Mierzenie względem arkusza zostawiało na A4 pas 12 mm (margines
  // strony), w którym blok przechodził walidację czysto i ZNIKAŁ z PDF-a:
  // kontener ma `overflow: hidden`, więc nie było ani błędu, ani śladu.
  // Dla etykiety margines wynosi zero, więc to nadal wymiary etykiety.
  const { widthMm, heightMm } = obszarZadrukuMm(media);
  const out: NaruszenieMediow[] = [];
  if (o.xMm < -LUZ_MM || o.yMm < -LUZ_MM || o.xMm > widthMm + LUZ_MM || o.yMm > heightMm + LUZ_MM) {
    out.push('poczatek');
  }
  if (!o.szacowanaSzerokosc && o.xMm + o.widthMm > widthMm + LUZ_MM) out.push('prawa');
  if (!o.szacowanaWysokosc && o.yMm + o.heightMm > heightMm + LUZ_MM) out.push('dol');
  return out;
}

/** Czy obrys mieści się w mediach w całości (obie osi pewne). */
export function mieciSieWMediach(o: Obrys, media: PrintMedia): boolean {
  return naruszeniaMediow(o, media).length === 0;
}
